// Proactive cache warmer.
//
// On app boot — when we know we're online AND a user is signed in — fire a
// small set of background fetches for endpoints the user will need offline
// later (band/contact/venue pickers, etc).
//
// Why proactive instead of lazy:
//   - The stale-while-revalidate cache only knows what the user has already
//     visited. A musician who opens the app at home (online) and then loses
//     signal at the venue should be able to ADD a gig — which means the
//     band/collaborator/venue pickers need data.
//   - These endpoints are cheap (<5KB each typically) and rarely change.
//
// Design notes:
//   - Run AFTER auth resolves so we know we have a session.
//   - Run in background — don't block UI.
//   - Throttle: only warm once per N minutes to avoid wasted bandwidth on
//     SPA route changes that re-mount the AuthProvider.
//   - Tolerant: any individual fetch failure is logged and skipped; never
//     surfaces an error to the user.

import { setMeta, getMeta } from "./offlineCache";

const LAST_WARM_KEY = "last_cache_warm_at";
const THROTTLE_MS = 5 * 60 * 1000; // 5 minutes

// Endpoints worth pre-fetching. Keep this list tight — adding more increases
// bandwidth and storage with diminishing returns. Each entry MUST be a
// safe-to-cache GET. Avoid endpoints whose response varies by request time
// (e.g. "today's reminders").
const WARM_TARGETS = [
  "/bands",
  "/collaborators",
  "/venues",
  "/venues?public_only=true",
  "/setlists",
  "/user/travel-settings",
  "/calendar/all-events",
  // Gig lists — though Dashboard usually fetches these, warming guarantees
  // they're cached even if user lands on an unrelated route first.
  "/gigs?time_filter=upcoming",
  "/gigs?time_filter=all",
];

export const warmCache = async (apiClient, { force = false } = {}) => {
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    return { skipped: "offline" };
  }
  if (!force) {
    const last = await getMeta(LAST_WARM_KEY);
    if (last && Date.now() - last < THROTTLE_MS) {
      return { skipped: "throttled", lastAt: last };
    }
  }

  const results = await Promise.allSettled(
    WARM_TARGETS.map((url) => apiClient.get(url).catch((err) => Promise.reject(err)))
  );

  const summary = WARM_TARGETS.reduce((acc, url, i) => {
    acc[url] = results[i].status === "fulfilled" ? "ok" : "fail";
    return acc;
  }, {});

  await setMeta(LAST_WARM_KEY, Date.now());
  return summary;
};

// Convenience: defer until after first paint so we don't compete with the
// app's own page-load fetches for bandwidth.
export const scheduleWarm = (apiClient) => {
  const run = () => {
    warmCache(apiClient).catch(() => {});
  };
  if (typeof requestIdleCallback === "function") {
    requestIdleCallback(run, { timeout: 5000 });
  } else {
    setTimeout(run, 2500);
  }
};
